// 2024 character origins (SRD 5.2): the background, not the species, raises ability scores and grants an
// Origin feat, and every character knows Common plus two Standard languages. Pure rules shared by the
// creation wizard and the server create route.
export const ORIGIN_ABILITIES = Object.freeze(['str', 'dex', 'con', 'int', 'wis', 'cha']);
export const STANDARD_LANGUAGES = Object.freeze([
  'Common Sign Language', 'Draconic', 'Dwarvish', 'Elvish', 'Giant', 'Gnomish', 'Goblin', 'Halfling', 'Orc',
]);
export const ORIGIN_LANGUAGE_CHOICES = 2;
// Origin feats a Human may take with Versatile.
export const VERSATILE_FEATS = Object.freeze(['Alert', 'Magic Initiate', 'Savage Attacker', 'Skilled']);
// What each Origin feat does, for the creation wizard, which has no feat catalogue loaded. Matches the notes in
// server/data/feats.js (checked by originChoices.test.js).
export const ORIGIN_FEAT_NOTES = Object.freeze({
  Alert: 'Initiative Proficiency: add your Proficiency Bonus when you roll Initiative. Initiative Swap: immediately after you roll Initiative, you can swap Initiative with one willing ally in the same combat; neither of you can be Incapacitated.',
  'Magic Initiate': 'Learn two cantrips and one level 1 spell from the Cleric, Druid or Wizard list (Intelligence, Wisdom or Charisma is your spellcasting ability). You always have the spell prepared and can cast it once per Long Rest without a slot. When you gain a level you can swap one of these spells. Repeatable with a different list each time.',
  'Savage Attacker': 'Once per turn when you hit a target with a weapon, you can roll the weapon’s damage dice twice and use either roll.',
  Skilled: 'Gain proficiency in any combination of three skills or tools. Repeatable.',
});
// One line each, for comparing them on the Human's Versatile choice.
export const ORIGIN_FEAT_SUMMARIES = Object.freeze({
  Alert: 'Add your Proficiency Bonus to Initiative, and swap Initiative with a willing ally.',
  'Magic Initiate': 'Two cantrips and a level 1 spell from one list, castable free once per Long Rest.',
  'Savage Attacker': 'Once per turn, roll a weapon’s damage dice twice and use either roll.',
  Skilled: 'Proficiency in any three skills or tools.',
});
export const MAGIC_INITIATE_LISTS = Object.freeze(['cleric', 'druid', 'wizard']);
/** A feat's text for this character: Magic Initiate names its spell list once the background or the player has fixed one. */
export const originFeatNote = (featName, spellList) => {
  const note = ORIGIN_FEAT_NOTES[featName] || '';
  if (featName !== 'Magic Initiate' || !MAGIC_INITIATE_LISTS.includes(spellList)) return note;
  return note.replace('the Cleric, Druid or Wizard list', `the ${spellList[0].toUpperCase()}${spellList.slice(1)} list`);
};

/** An error message for a background increase, or null. Valid: +2/+1 or +1/+1/+1 across its three abilities. */
export const backgroundIncreaseError = (background, increase) => {
  const allowed = Array.isArray(background?.abilityScores) ? background.abilityScores : [];
  if (allowed.length !== 3) return 'Choose a 2024 background.';
  if (!increase || typeof increase !== 'object' || Array.isArray(increase)) return 'Choose your background ability increases.';
  const entries = Object.entries(increase).filter(([, value]) => Number(value) !== 0);
  if (entries.some(([ability]) => !allowed.includes(ability))) return `${background.name} can only increase ${allowed.map(a => a.toUpperCase()).join(', ')}.`;
  const values = entries.map(([, value]) => Number(value)).sort((a, b) => b - a).join(',');
  if (values !== '2,1' && values !== '1,1,1') return 'Increase one ability by 2 and another by 1, or all three by 1.';
  return null;
};

/** Apply a validated increase to base scores, never above 20. */
export const applyBackgroundIncrease = (scores, increase = {}) => Object.fromEntries(ORIGIN_ABILITIES.map(ability => [
  ability, Math.min(20, (Number(scores?.[ability]) || 0) + (Number(increase?.[ability]) || 0)),
]));

/** An error message for the two chosen Standard languages, or null. */
export const originLanguagesError = (languages) => {
  if (!Array.isArray(languages) || languages.length !== ORIGIN_LANGUAGE_CHOICES) return 'Choose two Standard languages besides Common.';
  if (new Set(languages).size !== languages.length) return 'Choose two different languages.';
  if (languages.some(language => !STANDARD_LANGUAGES.includes(language))) return 'Choose languages from the Standard Languages list.';
  return null;
};

export const isVersatileSpecies = (race) => String(race?.name || '').trim().toLowerCase() === 'human';
